import { reloadOnBackCache } from "./session";
import { useCallback, useEffect, useMemo, useState } from "react";
import { portalApi } from "./api";
import { statusOf } from "./errors";
import { I18nContext, isLang, translator, useI18n } from "./i18n";
import { hasBack, useNavigation, type Route } from "./routes";
import { PortalStateContext, usePortal, type PortalState } from "./state";
import { applyIcon, applySkin } from "./theme";
import type { Lang, PortalContext, Profile } from "./types";
import { Link, Loading } from "./ui";
import { BackButton, Footer, Header, SideNav } from "./chrome";
import { playShowsAll } from "@shared/consumer-tvod";
import { BrowseScreen } from "./screens/Browse";
import { TitleScreen } from "./screens/Title";
import { GenreScreen } from "./screens/Genre";
import { MyContentScreen } from "./screens/MyContent";
import { PlanScreen } from "./screens/Plan";
import { ViewerHome } from "./screens/ViewerHome";
import {
  ConfirmScreen,
  ForgotScreen,
  InviteScreen,
  LoginScreen,
  ApplyScreen,
  ResendScreen,
  ResetScreen,
} from "./screens/Auth";
import { AccountScreen } from "./screens/Account";
import Watch from "./screens/Watch";
import { InformationItemScreen, InformationScreen } from "./information";

/**
 * The consumer purchase portal (consumer TVOD, wave 2 slice E;
 * `docs/design/consumer-tvod.md` §1, §3.2, §4, §5, §7).
 *
 * ONE build serves every provider's portal host. On load it asks
 * `GET /api/interactiveplayer/v1/portal/context` which provider this host is; an unknown, pending
 * or disabled host answers one identical 404 and gets one neutral page,
 * in both languages because there is no portal to take a locale from.
 *
 * Signed-in state is a probe of `GET /me` (with one refresh on a 401, which
 * `api.ts` does), answered before any screen renders so no screen draws a
 * signed-out state over a live session.
 *
 * Catalogue and player in one site (PLAY-10, Sigge 2026-10-06): one domain per
 * provider. `/watch/:id` is the web player - the engine shared with the TV
 * (`src/player`), full window, signed in only.
 */

const LANG_KEY = "foyer-portal:lang";

function storedLang(): Lang | null {
  try {
    const v = window.localStorage.getItem(LANG_KEY);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
}

function storeLang(lang: Lang) {
  try {
    window.localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* private mode: the choice lasts this page only */
  }
}

type Boot =
  | { state: "loading" }
  | { state: "unavailable" }
  | { state: "error" }
  | { state: "ready"; context: PortalContext };

export default function App() {
  const nav = useNavigation();
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  const [me, setMe] = useState<Profile | null | undefined>(undefined);
  const [chosenLang, setChosenLang] = useState<Lang | null>(storedLang);
  const [loginEmail, setLoginEmail] = useState("");

  const load = useCallback(async () => {
    setBoot({ state: "loading" });
    try {
      const context = await portalApi.context();
      setBoot({ state: "ready", context });
    } catch (err) {
      setBoot(
        statusOf(err) === 404 ? { state: "unavailable" } : { state: "error" }
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const reloadMe = useCallback(async () => {
    try {
      const profile = await portalApi.me();
      setMe(profile);
      return profile;
    } catch {
      setMe(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (boot.state === "ready") void reloadMe();
  }, [boot.state, reloadMe]);

  const context = boot.state === "ready" ? boot.context : null;

  useEffect(() => {
    applySkin(context?.skin.theme ?? null, context?.appearance ?? null);
    if (context) {
      document.title = context.portal.name;
      applyIcon(
        context.skin.theme?.logoUrl,
        context.appearance?.faviconUrl ?? null
      );
    }
  }, [context]);

  const lang: Lang =
    chosenLang ??
    (me?.locale && isLang(me.locale) ? me.locale : null) ??
    (context && isLang(context.settings.defaultLocale)
      ? context.settings.defaultLocale
      : "sv");

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // A page restored from the back/forward cache shows whoever was signed in
  // when it was left: reload so it asks who is signed in now.
  useEffect(() => reloadOnBackCache(), []);

  const i18n = useMemo(() => ({ lang, t: translator(lang) }), [lang]);

  const chooseLang = useCallback((next: Lang) => {
    setChosenLang(next);
    storeLang(next);
  }, []);

  if (boot.state === "loading") {
    return (
      <I18nContext.Provider value={i18n}>
        <div className="app">
          <main className="main wrap">
            <Loading />
          </main>
        </div>
      </I18nContext.Provider>
    );
  }
  if (boot.state === "unavailable") return <Unavailable />;
  if (boot.state === "error") return <BootError onRetry={load} />;

  const state: PortalState = {
    context: boot.context,
    me: me ?? null,
    setMe,
    reloadMe,
    go: nav.go,
    replace: nav.replace,
    back: nav.back,
    loginEmail,
    setLoginEmail,
  };

  // The player takes the whole window: no rail, header or footer beside a
  // film. Signed out, it is the sign-in first, then straight back here.
  if (nav.route.name === "watch") {
    return (
      <I18nContext.Provider value={i18n}>
        <PortalStateContext.Provider value={state}>
          {me === undefined ? (
            <Loading />
          ) : me === null ? (
            <SignInFirst
              next={`/watch/${encodeURIComponent(nav.route.assetId)}`}
            />
          ) : (
            <Watch key={nav.route.assetId} assetId={nav.route.assetId} />
          )}
        </PortalStateContext.Provider>
      </I18nContext.Provider>
    );
  }

  return (
    <I18nContext.Provider value={i18n}>
      <PortalStateContext.Provider value={state}>
        {/* A visitor gets no side menu (organisation product: nothing to
            browse to before signing in or applying). */}
        <div className={me ? "app app--rail" : "app"}>
          <a className="skip" href="#main">
            {i18n.t("nav.skip")}
          </a>
          {me && <SideNav kind={me.kind} route={nav.route} />}
          <div className="app__column">
            <Header
              signedIn={!!me}
              route={nav.route}
              lang={lang}
              onLang={chooseLang}
              hideSearch={
                !!me &&
                (me.kind === "viewer" ||
                  (me.kind !== "purchaser" &&
                    !playShowsAll(boot.context.settings)))
              }
            />
            <main className="main wrap" id="main" data-route={nav.route.name}>
              {hasBack(nav.route) && <BackButton />}
              {me === undefined ? <Loading /> : <Screen route={nav.route} />}
            </main>
            <Footer />
          </div>
        </div>
      </PortalStateContext.Provider>
    </I18nContext.Provider>
  );
}

function Screen({ route }: { route: Route }) {
  const { me, context } = usePortal();
  // ORG-6: a play account sees only its "Mitt innehåll" - plus the title
  // page (a series' episodes), the player and its own account.
  if (
    me?.kind === "viewer" &&
    ![
      "title",
      "watch",
      "account",
      "login",
      "invite",
      "reset",
      // The provider's Information rail is for every member (2026-10-09).
      "information",
      "information-item",
    ].includes(route.name)
  ) {
    return <ViewerHome />;
  }
  switch (route.name) {
    case "browse":
      // "Inloggade medlemmar ser: Endast köpt innehåll" (CMS): a signed-in
      // member's home is what they have rented; a visitor sees the catalogue.
      // A purchaser picks and never watches, so it always gets the catalogue.
      return me &&
        me.kind !== "purchaser" &&
        !playShowsAll(context.settings) ? (
        <MyContentScreen />
      ) : (
        <BrowseScreen q={route.q} />
      );
    case "genre":
      return (
        <GenreScreen
          key={route.genreId ?? ""}
          genreId={route.genreId}
          sort={route.sort}
        />
      );
    case "title":
      return <TitleScreen key={route.assetId} assetId={route.assetId} />;
    case "my":
      return <MyContentScreen />;
    case "plan":
      // Only an organisation's purchaser has a subscription page.
      return me?.kind === "purchaser" ? <PlanScreen /> : <BrowseScreen q="" />;
    case "account":
      return <AccountScreen />;
    case "login":
      return <LoginScreen next={route.next} />;
    case "register":
      return <ApplyScreen />;
    case "confirm":
      return <ConfirmScreen />;
    case "resend":
      return <ResendScreen />;
    case "forgot":
      return <ForgotScreen />;
    case "reset":
      return <ResetScreen />;
    case "invite":
      return <InviteScreen />;
    case "information":
      return me ? <InformationScreen /> : <SignInFirst next="/information" />;
    case "information-item":
      return me ? (
        <InformationItemScreen key={route.id} id={route.id} />
      ) : (
        <SignInFirst next={`/information/${encodeURIComponent(route.id)}`} />
      );
    case "watch":
      // Rendered full-window by App, never inside the chrome.
      return null;
    case "not-found":
      return <NotFound />;
  }
}

/** Playing needs an account: off to the sign-in, which returns here. */
function SignInFirst(props: { next: string }) {
  const { replace } = usePortal();
  useEffect(() => {
    replace(`/login?next=${encodeURIComponent(props.next)}`);
  }, [replace, props.next]);
  return <Loading />;
}

function NotFound() {
  const { t } = useI18n();
  return (
    <section className="panel">
      <h1 className="page-title">{t("title.notFound")}</h1>
      <p>
        <Link to="/">{t("return.browse")}</Link>
      </p>
    </section>
  );
}

/**
 * The neutral page for a host that is not a live portal. It names no
 * provider and does not say why: unknown, pending and switched-off are one
 * answer on the wire and one page here. Both languages, since there is no
 * portal to take a locale from.
 */
export function Unavailable() {
  const sv = translator("sv");
  return (
    <div className="app app--bare">
      <main className="main" id="main">
        <section className="panel" lang="sv">
          <h1 className="page-title">{sv("unavailable.title")}</h1>
          <p>{sv("unavailable.body")}</p>
        </section>
        <section className="panel" lang="en">
          <h2>{sv("unavailable.titleEn")}</h2>
          <p>{sv("unavailable.bodyEn")}</p>
        </section>
      </main>
    </div>
  );
}

function BootError(props: { onRetry(): void }) {
  const { t } = useI18n();
  return (
    <div className="app app--bare">
      <main className="main">
        <section className="panel">
          <p role="alert">{t("common.offline")}</p>
          <button type="button" className="btn" onClick={props.onRetry}>
            {t("common.retry")}
          </button>
        </section>
      </main>
    </div>
  );
}
